import { WriteBacks } from '@getvim/vim-connect';
import { useCallback } from 'react';
import { orderAssistLogger } from '../../../../logger';
import { useSdkWriteback, useSdkOrderWriteback } from './useSdkReferralWriteback';
import {
  DEFAULT_NUMBER,
  type UseWriteBack,
  type WritebackReferralInput,
  type WritebackOrderInput,
} from './types';
import { WriteBackProvider } from '../../../../types';
import { isEmpty } from 'lodash-es';
import { formatFacility } from '../../../../utils/writebackFormatters';

export const useWriteBack = (): UseWriteBack => {
  const writebackReferral = useSdkWriteback();
  const writebackOrder = useSdkOrderWriteback();

  const writeProvider: UseWriteBack['writeProvider'] = useCallback(
    async (targetProvider) => {
      try {
        const formattedProvider = targetProvider
          ? `Vim Select ${WriteBacks.formatProvider(targetProvider, true)}`
          : undefined;

        const data: WritebackReferralInput = {
          targetProvider: verifyTargetProvider(targetProvider),
          notes: [formattedProvider].filter(Boolean).join('\n'),
        };

        await writebackReferral(data);

        return { success: true };
      } catch (error: unknown) {
        orderAssistLogger.error('Failed to writeback referral target provider', {
          targetProvider,
          error,
        });

        const errorResult =
          (error as { errorName?: string; message?: string })?.errorName ||
          (error as { errorName?: string; message?: string })?.message;
        return { success: false, error: errorResult };
      }
    },
    [writebackReferral],
  );

  const writeAuthCode: UseWriteBack['writeAuthCode'] = useCallback(
    async (authCode) => {
      try {
        const data: WritebackReferralInput = {
          authCode,
        };

        await writebackReferral(data, { clearEmptyFields: false });
        return { success: true };
      } catch (error: unknown) {
        orderAssistLogger.error('Failed to writeback referral authCode', {
          authCode,
          error,
        });
        return { success: false, error: (error as { message?: string })?.message };
      }
    },
    [writebackReferral],
  );

  const writeFacility: UseWriteBack['writeFacility'] = useCallback(
    async (targetFacility) => {
      try {
        const formattedFacility = targetFacility
          ? `Vim Select ${formatFacility(targetFacility)}`
          : undefined;

        const data: WritebackOrderInput = {
          targetProvider: verifyTargetProvider(targetFacility),
          notes: formattedFacility,
        };

        await writebackOrder(data);

        return { success: true };
      } catch (error: unknown) {
        orderAssistLogger.error('Failed to writeback order target facility', {
          targetFacility,
          error,
        });

        const errorResult =
          (error as { errorName?: string; message?: string })?.errorName ||
          (error as { errorName?: string; message?: string })?.message;
        return { success: false, error: errorResult };
      }
    },
    [writebackOrder],
  );

  return { writeProvider, writeAuthCode, writeFacility };
};

const verifyTargetProvider = (providerToWrite: WriteBackProvider): WriteBackProvider => {
  const { organizations } = providerToWrite ?? {};
  const organization = organizations?.[0];

  if (isEmpty(organization)) {
    orderAssistLogger.warning('No organization found in provider to write, skipping verification', {
      providerToWrite,
    });
    return providerToWrite;
  }

  const { contact_info } = organization;
  const phoneNumber = contact_info?.mobilePhoneNumber?.trim();
  const faxNumber = contact_info?.faxNumber?.trim();

  if (!isEmpty(phoneNumber) && !isEmpty(faxNumber)) {
    return providerToWrite;
  }

  const verifiedNumber = isEmpty(phoneNumber) ? DEFAULT_NUMBER : phoneNumber;
  const verifiedFax = isEmpty(faxNumber) ? DEFAULT_NUMBER : faxNumber;

  return {
    ...providerToWrite,
    organizations: [
      {
        ...organization,
        contact_info: {
          ...organization?.contact_info,
          mobilePhoneNumber: verifiedNumber,
          faxNumber: verifiedFax,
        },
      },
    ],
  };
};
